import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import React from 'react';
import {List} from '@Components/list';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {FlatIconColor, FlatIconColorType, IconSize} from '@Components/icon-v2/icon.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {TextSize} from '@Components/text';
import useMergeTags from '@Hooks/merge-tags/useMergeTags';
import styles from './merge-tags-dropdown.module.scss';
import type {MergeTagsDropdownProps} from './merge-tags-dropdown.types';
import type {MergeTag} from '@Components/merge-tags/merge-tags.types';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {addClickOutsideListener} from '@Utils/dom.util';

export function MergeTagsDropdown({listId, onSelect, ...props}: MergeTagsDropdownProps): ReactElement {
  const {mergeTagOptions} = useMergeTags();
  const mergeTagKeySeperatorForListItemId = '--';
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!showDropdown) return;

    const dropdownId = `#${listId}-dropdown`;
    const hideDropdown = (): void => {
      setShowDropdown(false);
    };
    const removeListener = addClickOutsideListener(dropdownId, hideDropdown);

    return () => {
      removeListener();
    };
  }, [showDropdown]);

  const getItems = (): ListItemStorage[] => {
    return mergeTagOptions.map((tag) => {
      return {
        id: getListItemIdFromMergeTagKey(tag.mergeTagKey),
        isSelected: false,
        text: window.i18next.t(tag.label),
        subtext: '',
        type: LIST_ITEM_TYPE.DEFAULT_2,
        thumbnailClassName: styles.mergeTagOptionThumbnail,
        thumbnail: {
          type: ThumbnailType.ICON,
          icon: tag.icon,
          icon_size: IconSize.SIZE_ICON_20,
          flat_icon_type: {
            type: FlatIconColorType.DEFAULT,
            color: FlatIconColor.SECONDARY,
          },
        },
        onClick: (listId, listItemId?: string): void => {
          if (!listItemId) {
            return;
          }

          const mergeTagKey = getMergeTagKeyFromListItemId(listItemId);

          if (!mergeTagKey) {
            return;
          }

          const source = listId?.split('-')[0] ?? false;
          if (source) {
            window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_MERGE_TAGS_ADDED, {[GA4EventParamName.SOURCE]: source, [GA4EventParamName.MERGE_TAGS]: mergeTagKey});
          }

          onSelect(mergeTagKey);
        },
        textSize: TextSize.SMALL,
        textClasses: styles.mergeTagOptionText,
        className: `spacing-p-3 ${styles.item}`,
      };
    });
  };

  const getListItemIdFromMergeTagKey = (mergeTagKey: string): string => {
    return `${listId}${mergeTagKeySeperatorForListItemId}${mergeTagKey}`;
  };

  const getMergeTagKeyFromListItemId = (listItemId: string): MergeTag['mergeTagKey'] | null => {
    const idParts = listItemId.split(mergeTagKeySeperatorForListItemId);

    if (idParts.length < 2) {
      return '';
    }

    return idParts[1];
  };

  const handleOnClick = (): void => {
    if (showDropdown) {
      return;
    }

    setShowDropdown(true);
  };

  return (
    <Dropdown
      selector={<div className="size-icon-16 icon-tags" />}
      id={`${listId}-dropdown`}
      key={`${listId}-dropdown`}
      popup={<List id={listId} multiSelect={false} items={getItems()} className={`_full-width ${styles.list}`} />}
      popupSpacingClass="spacing-p-l-3 spacing-p-r-3 spacing-p-t-3 spacing-p-b-1 border-s-dark radius-8"
      alignment={ALIGNMENT_TYPE.BOTTOM_END}
      popupClassName={styles.popup}
      popUpHasCustomWidth
      popUpHasCustomHeight
      showModalForSmallScreen
      shouldFlip
      doCloseOnInnerClick
      doesCloseOnOuterClick
      modalTitle={window.i18next.t('pmwjs_merge_tags')}
      open={showDropdown}
      onClick={handleOnClick}
      {...props}
    />
  );
}
